import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid,
  MenuItem, CircularProgress, Tooltip, Alert, InputAdornment, Stack, Divider,
} from '@mui/material';
import {
  Add, Edit, Delete, Close, Search, AccountBalance,
  CheckCircle, Cancel, Refresh, CalendarMonth, Savings, Lock, InfoOutlined,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  bankAccountsURL, bankAccountsAddURL, bankAccountsUpdateURL, bankAccountsDeleteURL,
  accountingPeriodsURL,
} from '../BaseURL';

function headers() { return { Authorization: `Bearer ${localStorage.getItem('church_token')}` }; }
const swalTop = { customClass: { container: 'swal-on-top' } };
const EMPTY = { name: '', opening_balance: '', opening_date: '', status: 'active' };

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-GH', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function BankAccounts() {
  const [accounts, setAccounts]   = useState([]);
  const [activePeriod, setActivePeriod] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [loadError, setLoadError] = useState('');
  const [open, setOpen]           = useState(false);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(EMPTY);
  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState('');
  const [search, setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  useEffect(() => {
    if (!document.getElementById('swal-top')) {
      const s = document.createElement('style');
      s.id = 'swal-top'; s.innerHTML = '.swal-on-top{z-index:99999!important}';
      document.head.appendChild(s);
    }
  }, []);

  const loadPeriod = () => {
    axios.get(accountingPeriodsURL, { headers: headers() })
      .then(r => {
        if (r.data.success) {
          const active = (r.data.data || []).find(p => p.is_active);
          setActivePeriod(active || null);
        }
      })
      .catch(() => setActivePeriod(null));
  };

  const load = () => {
    setLoading(true); setLoadError('');
    axios.get(bankAccountsURL, { headers: headers() })
      .then(r => { if (r.data.success) setAccounts(r.data.data); else setLoadError(r.data.message); })
      .catch(err => setLoadError(err.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); loadPeriod(); }, []);

  const periodStart = activePeriod ? String(activePeriod.start_date).slice(0, 10) : '';
  const editingLocked = !!editing && Number(editing.tx_count || 0) > 0;

  const filtered = useMemo(() => accounts.filter(a => {
    const q = search.toLowerCase().trim();
    const matchesSearch = !q || a.name.toLowerCase().includes(q);
    const matchesStatus = statusFilter === 'ALL' || a.status === statusFilter;
    return matchesSearch && matchesStatus;
  }), [accounts, search, statusFilter]);

  const activeCount = useMemo(() => accounts.filter(a => a.status === 'active').length, [accounts]);
  const totalOpening = useMemo(
    () => accounts.reduce((s, a) => s + Number(a.opening_balance || 0), 0),
    [accounts]
  );

  const openAdd = () => {
    if (!activePeriod) return;
    setEditing(null); setSaveError('');
    setForm({ ...EMPTY, opening_date: periodStart });
    setOpen(true);
  };
  const openEdit = (a) => {
    setEditing(a); setSaveError('');
    setForm({
      name: a.name,
      opening_balance: a.opening_balance,
      opening_date: a.opening_date?.slice(0, 10) || periodStart,
      status: a.status,
    });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setSaveError('Account name is required.'); return; }
    setSaving(true); setSaveError('');
    try {
      if (editing) {
        // Backend decides what's locked; only send fields the user is allowed to change.
        const payload = editingLocked
          ? { id: editing.id, name: form.name, status: form.status }
          : { id: editing.id, name: form.name, opening_balance: form.opening_balance, status: form.status };
        await axios.post(bankAccountsUpdateURL, payload, { headers: headers() });
      } else {
        // Opening date is forced by the backend to the active period start; we omit it.
        const { opening_date: _ignored, ...payload } = form;
        await axios.post(bankAccountsAddURL, payload, { headers: headers() });
      }
      setOpen(false); load();
      Swal.fire({ ...swalTop, icon: 'success', title: editing ? 'Updated!' : 'Account Added!', timer: 1500, showConfirmButton: false });
    } catch (err) { setSaveError(err.response?.data?.message || 'Could not save.'); }
    finally { setSaving(false); }
  };

  const handleDelete = (a) => {
    if (Number(a.tx_count || 0) > 0) {
      Swal.fire({
        ...swalTop, icon: 'info', title: 'Cannot delete',
        text: `"${a.name}" has ${a.tx_count} transaction(s). Mark it inactive instead.`,
      });
      return;
    }
    Swal.fire({
      ...swalTop, title: `Delete "${a.name}"?`,
      text: 'This account has no transactions and will be removed.',
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#d32f2f', confirmButtonText: 'Delete',
    }).then(async r => {
      if (r.isConfirmed) {
        try { await axios.post(bankAccountsDeleteURL, { id: a.id }, { headers: headers() }); load(); }
        catch (err) { Swal.fire({ ...swalTop, icon: 'error', title: 'Error', text: err.response?.data?.message || 'Could not delete.' }); }
      }
    });
  };

  const statusCounts = {
    ALL: accounts.length,
    active: accounts.filter(a => a.status === 'active').length,
    inactive: accounts.filter(a => a.status === 'inactive').length,
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2.5} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#0B2545' }}>Bank Accounts</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage the bank accounts used to receive offerings, tithes, and other church income.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {activePeriod && (
            <Chip
              icon={<CalendarMonth sx={{ fontSize: 16 }} />}
              label={`${activePeriod.name} · ${fmtDate(activePeriod.start_date)} → ${fmtDate(activePeriod.end_date)}`}
              size="small"
              sx={{ fontWeight: 600, bgcolor: '#0B254518', color: '#0B2545' }}
            />
          )}
          <Button variant="outlined" startIcon={<Refresh />} onClick={() => { load(); loadPeriod(); }}>Refresh</Button>
          <Tooltip title={activePeriod ? '' : 'Create and activate an accounting period first (Settings → Accounting Periods)'}>
            <span>
              <Button
                variant="contained" startIcon={<Add />} onClick={openAdd}
                disabled={!activePeriod}
                sx={{ bgcolor: '#0B2545', '&:hover': { bgcolor: '#13315C' } }}
              >
                Add Account
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {!activePeriod && (
        <Alert severity="warning" icon={<InfoOutlined />} sx={{ mb: 2 }}>
          <strong>No active accounting period.</strong> Bank accounts can only be created inside an active period so opening balances align to a period boundary. Ask an admin to create one under <em>Settings → Accounting Periods</em>.
        </Alert>
      )}

      {loadError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLoadError('')}>{loadError}</Alert>}

      {/* Stat cards */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderLeft: '4px solid #0B2545' }}>
            <CardContent sx={{ py: '14px !important' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>TOTAL ACCOUNTS</Typography>
              <Typography variant="h4" fontWeight={700} sx={{ color: '#0B2545' }}>{accounts.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderLeft: '4px solid #2e7d32' }}>
            <CardContent sx={{ py: '14px !important' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>ACTIVE</Typography>
              <Typography variant="h4" fontWeight={700} sx={{ color: '#2e7d32' }}>{activeCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderLeft: '4px solid #C89211' }}>
            <CardContent sx={{ py: '14px !important' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>TOTAL OPENING BALANCE</Typography>
              <Typography variant="h4" fontWeight={700} sx={{ color: '#C89211' }}>
                GH₵ {fmtMoney(totalOpening)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: '14px !important' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField
              size="small" placeholder="Search by account name…"
              value={search} onChange={e => setSearch(e.target.value)}
              sx={{ width: { xs: '100%', md: 360 } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            />
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
            <Box display="flex" gap={1} flexWrap="wrap">
              {[
                { key: 'ALL', label: 'All', color: '#0B2545' },
                { key: 'active', label: 'Active', color: '#2e7d32' },
                { key: 'inactive', label: 'Inactive', color: '#757575' },
              ].map(f => (
                <Chip
                  key={f.key} size="small" label={`${f.label} (${statusCounts[f.key] || 0})`}
                  onClick={() => setStatusFilter(f.key)}
                  sx={{
                    fontWeight: 600, cursor: 'pointer',
                    bgcolor: statusFilter === f.key ? f.color : f.color + '18',
                    color: statusFilter === f.key ? '#fff' : f.color,
                  }}
                />
              ))}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: '#f7f9fc', fontWeight: 700, color: '#0B2545' } }}>
                <TableCell width={50}>#</TableCell>
                <TableCell>Account Name</TableCell>
                <TableCell align="right">Opening Balance</TableCell>
                <TableCell>Opening Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center" width={110}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6 }}><CircularProgress size={30} /></TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    <AccountBalance sx={{ fontSize: 40, color: '#c9d3e0', mb: 1 }} />
                    <Typography variant="body2">
                      {accounts.length === 0 ? <>No bank accounts yet. Click <strong>"Add Account"</strong> to create your first.</> : 'No results match your filter.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filtered.map((a, i) => (
                <TableRow key={a.id} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                  <TableCell sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1.2}>
                      <Box sx={{
                        width: 32, height: 32, borderRadius: 1,
                        bgcolor: '#0B254518', color: '#0B2545',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <AccountBalance sx={{ fontSize: 18 }} />
                      </Box>
                      <Typography variant="body2" fontWeight={700} sx={{ color: '#0B2545' }}>{a.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={700} sx={{ color: '#2e7d32', fontFamily: 'monospace' }}>
                      GH₵ {fmtMoney(a.opening_balance)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.6} sx={{ color: 'text.secondary' }}>
                      <CalendarMonth sx={{ fontSize: 15 }} />
                      <Typography variant="body2">{fmtDate(a.opening_date)}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={a.status === 'active' ? <CheckCircle sx={{ fontSize: 14 }} /> : <Cancel sx={{ fontSize: 14 }} />}
                      label={a.status} size="small"
                      color={a.status === 'active' ? 'success' : 'default'}
                      sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit"><IconButton size="small" sx={{ color: '#0B2545' }} onClick={() => openEdit(a)}><Edit fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(a)}><Delete fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {filtered.length > 0 && (
          <Box sx={{ px: 2, py: 1.2, borderTop: '1px solid #eef2f6', bgcolor: '#fafbfc' }}>
            <Typography variant="caption" color="text.secondary">
              Showing <strong>{filtered.length}</strong> of <strong>{accounts.length}</strong> account(s)
            </Typography>
          </Box>
        )}
      </Card>

      {/* Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          bgcolor: '#0B2545', color: '#fff', py: 1.5,
        }}>
          <Box display="flex" alignItems="center" gap={1}>
            {editing ? <Edit /> : <AccountBalance />}
            <Typography variant="h6" fontWeight={700}>
              {editing ? 'Edit Bank Account' : 'Add Bank Account'}
            </Typography>
          </Box>
          <IconButton onClick={() => setOpen(false)} size="small" sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 3 }}>
          {saveError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError('')}>{saveError}</Alert>}

          {/* Period rule banner */}
          {activePeriod && (
            <Alert
              severity={editingLocked ? 'info' : 'info'}
              icon={editingLocked ? <Lock /> : <InfoOutlined />}
              sx={{ mb: 2, alignItems: 'flex-start' }}
            >
              {editingLocked ? (
                <>
                  <strong>Opening balance is locked.</strong> This account already has{' '}
                  <strong>{editing.tx_count}</strong> transaction(s) recorded against it.
                  Only the name and status can be edited. To adjust the balance,
                  post a correcting journal entry in the Cash Book.
                </>
              ) : (
                <>
                  Active period: <strong>{activePeriod.name}</strong>
                  {' '}({fmtDate(activePeriod.start_date)} – {fmtDate(activePeriod.end_date)}).
                  The opening balance is recognised as at the period start
                  <strong> {fmtDate(activePeriod.start_date)}</strong>. This keeps balances
                  aligned to period boundaries and prevents mid-period drift.
                </>
              )}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Account Name *" value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value.toUpperCase() })}
                inputProps={{ style: { textTransform: 'uppercase', fontWeight: 600 } }}
                placeholder="e.g. GCB SAVINGS, BAPTRUST, SG GHANA"
                helperText="Use a clear, uppercase name for easy identification"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Opening Balance" type="number"
                value={form.opening_balance}
                onChange={e => setForm({ ...form, opening_balance: e.target.value })}
                disabled={editingLocked}
                placeholder="0.00"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box display="flex" alignItems="center" gap={0.5} sx={{ color: '#2e7d32' }}>
                        <Savings sx={{ fontSize: 16 }} />
                        <Typography variant="caption" fontWeight={700}>GH₵</Typography>
                      </Box>
                    </InputAdornment>
                  ),
                  endAdornment: editingLocked ? (
                    <InputAdornment position="end">
                      <Tooltip title="Locked — transactions exist"><Lock sx={{ fontSize: 16, color: 'text.disabled' }} /></Tooltip>
                    </InputAdornment>
                  ) : null,
                  sx: { fontFamily: 'monospace', fontWeight: 700 },
                }}
                helperText={editingLocked ? 'Cannot change — see banner above' : 'Balance brought forward at period start'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Opening Date" type="date"
                value={form.opening_date || periodStart}
                disabled
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarMonth sx={{ fontSize: 16, color: '#0B2545' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Auto-set to active period start"><Lock sx={{ fontSize: 16, color: 'text.disabled' }} /></Tooltip>
                    </InputAdornment>
                  ),
                }}
                helperText="Fixed to the start of the active accounting period"
              />
            </Grid>
            {editing && (
              <Grid item xs={12} sm={6}>
                <TextField
                  select fullWidth size="small" label="Status" value={form.status}
                  onChange={e => setForm({ ...form, status: e.target.value })}
                >
                  <MenuItem value="active">
                    <Box display="flex" alignItems="center" gap={1}>
                      <CheckCircle sx={{ fontSize: 14, color: '#2e7d32' }} /> Active
                    </Box>
                  </MenuItem>
                  <MenuItem value="inactive">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Cancel sx={{ fontSize: 14, color: '#757575' }} /> Inactive
                    </Box>
                  </MenuItem>
                </TextField>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: '#fafbfc' }}>
          <Button onClick={() => setOpen(false)} variant="outlined">Cancel</Button>
          <Button
            onClick={handleSave} variant="contained" disabled={saving}
            sx={{ bgcolor: '#0B2545', '&:hover': { bgcolor: '#13315C' }, minWidth: 130 }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : editing ? 'Save Changes' : 'Add Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
